import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BenchmarkPayload,
  BillingInterval,
  EntitlementSummary,
  ExamCode,
  PublicUser,
  RevisionPlanPayload,
  ServedQuestion,
  ReviewedQuestion,
  SessionSummary,
} from '@titrate/shared';
import { api } from './api';

export interface AppMeta {
  app: string;
  disclaimer: string;
  shortDisclaimer: string;
  features: {
    googleOAuth: boolean;
    billing: boolean;
    ai: boolean;
    textbookSwap: boolean;
    email: boolean;
  };
}

export interface ExamInfo {
  id: string;
  code: ExamCode;
  name: string;
  description: string;
  questionCount: number;
  timeLimitSec: number;
  openBook: boolean;
  questionTypes: string[];
  permittedReferences: string[];
  benchmarkPaceSec: number;
  domains: { code: string; name: string; weightPct: number }[];
}

export const keys = {
  meta: ['meta'] as const,
  me: ['me'] as const,
  exams: ['exams'] as const,
  entitlements: ['entitlements'] as const,
  sessions: ['sessions'] as const,
  session: (id: string) => ['session', id] as const,
  review: (id: string) => ['review', id] as const,
  benchmarks: ['benchmarks'] as const,
  benchmark: (id: string) => ['benchmark', id] as const,
  progress: (code: string) => ['progress', code] as const,
  plans: ['revision-plans'] as const,
  pricing: ['pricing'] as const,
  tutor: (qid: string | null) => ['tutor', qid] as const,
};

export const useMeta = () => useQuery({ queryKey: keys.meta, queryFn: () => api.get<AppMeta>('/api/meta'), staleTime: Infinity });

export const useMe = () =>
  useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<{ user: PublicUser | null }>('/api/auth/me'),
    // The session cookie can expire mid-visit; re-check when the tab regains focus.
    refetchOnWindowFocus: true,
  });

export const useExams = () =>
  useQuery({ queryKey: keys.exams, queryFn: () => api.get<{ exams: ExamInfo[] }>('/api/exams'), staleTime: 5 * 60_000 });

export const useEntitlements = () =>
  useQuery({
    queryKey: keys.entitlements,
    queryFn: () => api.get<{ entitlements: EntitlementSummary }>('/api/account/entitlements'),
  });

export const useSessions = () =>
  useQuery({ queryKey: keys.sessions, queryFn: () => api.get<{ sessions: SessionSummary[] }>('/api/sessions') });

export interface SessionPaper {
  session: {
    id: string;
    examCode: ExamCode;
    mode: 'diagnostic' | 'baseline' | 'practice';
    startedAt: string;
    completedAt: string | null;
    timeLimitSec: number | null;
    remainingSec: number | null;
    bookUseTracked: boolean;
    permittedReferences: string[];
    bookUseNotice: string | null;
  };
  questions: ServedQuestion[];
  answers: Record<string, { answer: string | null; referencedBook: boolean | null }>;
}

export const useSessionPaper = (id: string) =>
  useQuery({ queryKey: keys.session(id), queryFn: () => api.get<SessionPaper>(`/api/sessions/${id}`), staleTime: Infinity });

export const useReview = (id: string) =>
  useQuery({
    queryKey: keys.review(id),
    queryFn: () => api.get<{ sessionId: string; examCode: ExamCode; questions: ReviewedQuestion[] }>(`/api/sessions/${id}/review`),
  });

export const useBenchmark = (id: string) =>
  useQuery({ queryKey: keys.benchmark(id), queryFn: () => api.get<{ benchmark: BenchmarkPayload }>(`/api/benchmarks/${id}`) });

export const useBenchmarks = () =>
  useQuery({ queryKey: keys.benchmarks, queryFn: () => api.get<{ benchmarks: BenchmarkPayload[] }>('/api/benchmarks') });

export interface ProgressPayload {
  examCode: ExamCode;
  benchmarkPaceSec: number;
  hasBaseline: boolean;
  totalAnswered: number;
  trend: { id: string; createdAt: string; accuracyScore: number; speedScore: number; bookRelianceScore: number | null; avgTimeSec: number; hasReport: boolean }[];
  baseline: { id: string; createdAt: string; accuracyScore: number; speedScore: number; bookRelianceScore: number | null } | null;
  latest: { id: string; createdAt: string; accuracyScore: number; speedScore: number; bookRelianceScore: number | null } | null;
  domains: {
    domainCode: string; domainName: string; weightPct: number;
    lifetimeAttempted: number; lifetimeAccuracyPct: number;
    baselineAccuracyPct: number | null; latestAccuracyPct: number | null; deltaPct: number | null;
  }[];
  cognitiveLevels: { name: string; attempted: number; correct: number; accuracyPct: number }[];
  topicStrengths: { tagName: string; attempted: number; correct: number; accuracyPct: number }[];
}

export const useProgress = (examCode: string) =>
  useQuery({ queryKey: keys.progress(examCode), queryFn: () => api.get<ProgressPayload>(`/api/progress/${examCode}`) });

export const useRevisionPlans = () =>
  useQuery({ queryKey: keys.plans, queryFn: () => api.get<{ plans: RevisionPlanPayload[] }>('/api/revision-plans') });

export interface PricingPlan {
  tier: 'free' | 'standard' | 'advanced';
  label: string;
  /** Price per interval, in USD cents. */
  priceUsdCents: Record<BillingInterval, number>;
  /** What each interval works out at per month, for the comparison line. */
  monthlyEquivalentCents: Record<BillingInterval, number>;
  /** Percent saved against paying monthly. 0 for the monthly interval itself. */
  savingPct: Record<BillingInterval, number>;
  features: string[];
  fullLengthSims: boolean;
  practiceQuestionsPerDay: number | null;
  reports: number | null;
  revisions: number | null;
  tutorMessages: number | null;
}

export interface PricingPayload {
  stripeConfigured: boolean;
  /** Cadences this deployment can actually sell. */
  intervals: BillingInterval[];
  plans: PricingPlan[];
}

export const usePricing = () =>
  useQuery({
    queryKey: keys.pricing,
    queryFn: () => api.get<PricingPayload>('/api/billing/plans'),
    staleTime: Infinity,
  });

/** Invalidates everything that can change after a credit is spent. */
export function useInvalidateEntitlements() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.entitlements });
  };
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSuccess: () => qc.clear(),
  });
}
